import {createContext, type ReactNode, use, useCallback, useMemo} from 'react'

import {VisibleFieldsContextProvider} from './VisibleFieldsProvider'
import {getConfigurationDirtyState, type ViewConfiguration, type ViewConfigurationOverrides} from './view-configuration'

const ConfigurationContext = createContext<ViewConfiguration | null>(null)
const DirtyContext = createContext<ReturnType<typeof getConfigurationDirtyState> | null>(null)
const ActionsContext = createContext<{
  setVisibleField: (identifier: string, visible: boolean) => void
  applyConfiguration: (changes: ViewConfigurationOverrides) => void
  reset: () => void
} | null>(null)

/**
 * Compares the applied view configuration with its saved baseline and derives dirty flags.
 * Owns no configuration state: every edit, visible fields included, goes to the host through
 * `onConfigurationChange` and comes back as `appliedConfiguration`. Also republishes the
 * applied visible fields through `VisibleFieldsContextProvider` so card consumers read them
 * without the host mounting a second provider. Key by account/view identity.
 *
 * `appliedConfiguration` is the configuration the host actually rendered against. Once the
 * saved-view page payload ships a controller-resolved envelope, the host passes that
 * envelope's applied configuration directly. Until then, the host resolves it locally from
 * URL overrides on top of the baseline. Either way the provider trusts
 * `appliedConfiguration` as-is; if the server rejects or falls back on an invalid URL
 * override, it reflects what actually got served, not what the URL requested.
 *
 * `baseline` is the persisted configuration used for dirty comparisons. Without a baseline,
 * dirty tracking is disabled.
 *
 * `applyConfiguration` passes all menu edits to `onConfigurationChange` in one batch, so a
 * URL-backed host can write them in a single navigation. The host owns the transport (URL
 * params today, mutation tomorrow).
 *
 * `onReset` is invoked when the user discards changes. Hosts implement it by clearing
 * whatever transport carries their overrides (for a URL-backed host: navigate back to the
 * baseline params).
 */
export function SavedViewConfigurationProvider({
  baseline,
  appliedConfiguration,
  onConfigurationChange,
  onReset,
  children,
}: {
  baseline?: ViewConfiguration
  appliedConfiguration: ViewConfiguration
  onConfigurationChange: (changes: ViewConfigurationOverrides) => void
  onReset: () => void
  children: ReactNode
}) {
  const dirty = useMemo(
    () => getConfigurationDirtyState(baseline ?? appliedConfiguration, appliedConfiguration),
    [baseline, appliedConfiguration],
  )

  const setVisibleField = useCallback(
    (identifier: string, visible: boolean) => {
      const current = appliedConfiguration.visibleFields
      if (current.includes(identifier) === visible) return
      onConfigurationChange({
        visibleFields: visible ? [...current, identifier] : current.filter(field => field !== identifier),
      })
    },
    [appliedConfiguration.visibleFields, onConfigurationChange],
  )
  const applyConfiguration = useCallback(
    (changes: ViewConfigurationOverrides) => {
      if (Object.keys(changes).length > 0) onConfigurationChange(changes)
    },
    [onConfigurationChange],
  )
  const actions = useMemo(
    () => ({setVisibleField, applyConfiguration, reset: onReset}),
    [setVisibleField, applyConfiguration, onReset],
  )

  const visibleFieldIdentifiers: ReadonlySet<string> = useMemo(
    () => new Set(appliedConfiguration.visibleFields),
    [appliedConfiguration.visibleFields],
  )

  return (
    <ActionsContext value={actions}>
      <ConfigurationContext value={appliedConfiguration}>
        <DirtyContext value={dirty}>
          <VisibleFieldsContextProvider identifiers={visibleFieldIdentifiers} setVisible={setVisibleField}>
            {children}
          </VisibleFieldsContextProvider>
        </DirtyContext>
      </ConfigurationContext>
    </ActionsContext>
  )
}

/** Returns the applied configuration. */
export function useSavedViewConfiguration() {
  const configuration = use(ConfigurationContext)
  if (configuration === null) throw new Error('useSavedViewConfiguration requires SavedViewConfigurationProvider')
  return configuration
}

export function useSavedViewConfigurationDirtyState() {
  const dirty = use(DirtyContext)
  if (dirty === null) throw new Error('useSavedViewConfigurationDirtyState requires SavedViewConfigurationProvider')
  return {dirty, isConfigurationDirty: Object.values(dirty).some(Boolean)}
}

export function useSavedViewConfigurationActions() {
  const actions = use(ActionsContext)
  if (actions === null) throw new Error('useSavedViewConfigurationActions requires SavedViewConfigurationProvider')
  return actions
}
